import React from 'react';
import PropTypes from 'prop-types';

import * as Sentry from '@sentry/browser';

import { ToastContext, CommentCard } from '@weezevent/nacre';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import useAppContext from '../../../../../context/useAppContext';
import LoadingCard from '../../../../../components/loadingCard';

import Style from './moderationcomment.module.css';

export function ModerationComment({ attendee, sync, isReadOnly = false, ...rest }) {
    const { t } = useTranslation();
    const locale = i18n.language;
    const [{ organization, event }] = useAppContext();
    const toast = React.useContext(ToastContext);

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const commentItems = React.useMemo(() => {
        const comments = attendee.comment?.length > 0 ? attendee.comment : null;
        if (!comments) {
            return [];
        }
        const sep = ' '.repeat(Boolean(attendee.first_name) && Boolean(attendee.last_name));
        return isReadOnly
            ? [
                  {
                      text: comments,
                      user: `${attendee.first_name}${sep}${attendee.last_name}`,
                      readOnly: t(`ticket.manager.attendees.detail.comment.read-only`)
                  }
              ]
            : [
                  {
                      text: comments,
                      user: `${attendee.first_name}${sep}${attendee.last_name}`
                  }
              ];
    }, [isReadOnly, attendee]);

    const onEdit = React.useCallback(
        (value, mode = 'save') => {
            setIsSubmitting(true);
            let _attendee = attendee.copy();

            _attendee.comment = value;

            delete _attendee.form;
            delete _attendee.rate;

            _attendee
                .save({
                    organizationId: organization.id,
                    eventId: event.id
                })
                .then(() => {
                    toast.success(t(`ticket.manager.attendees.detail.comment.${mode}Sucess`));
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                    toast.error(t(`ticket.manager.attendees.detail.comment.${mode}Error`));
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [attendee, organization, event, sync]
    );

    return (
        <div className={attendee.comment ? Style['separator'] : ''}>
            <CommentCard
                items={commentItems}
                displayInput={!(isReadOnly || Boolean(attendee.comment))}
                onSubmit={value => onEdit(value, 'save')}
                onDelete={() => onEdit('', 'delete')}
                onEdit={(_, value) => onEdit(value, 'edit')}
                loading={isSubmitting ? <LoadingCard loading={true} /> : false}
                tradDelete={t('ticket.common.action.delete')}
                tradEdit={t('ticket.common.action.edit')}
                tradEmpty={t('ticket.manager.attendees.detail.comment.noComments')}
                tradViewAll={t('ticket.manager.attendees.detail.comment.viewAll')}
                tradPlaceholder={t('ticket.manager.attendees.detail.comment.addCommentPlaceholder')}
                tradTitle={t('ticket.manager.attendees.detail.comment.titlemoderation')}
                tradViewMore={t('ticket.manager.attendees.detail.comment.viewMore')}
                locale={locale}
            />
        </div>
    );
}

ModerationComment.propTypes = {
    attendeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    legacyComment: PropTypes.string
};

ModerationComment.defaultProps = {
    legacyComment: null
};
